/**
 * MeetingKbBanner — toast offering a Granola meeting for Knowledge Base ingestion.
 *
 * Backed by granola_pending_meetings (staged by the Granola webhook — see
 * granola_webhook_routes.py) via useGranolaPending. "Add" calls
 * POST /v1/granola/pending/{id}/accept, which ingests the staged meeting
 * into the real knowledge base server-side; "Dismiss" calls
 * POST /v1/granola/pending/{id}/dismiss. With multiple
 * pending meetings, one is shown at a time with prev/next navigation
 * (same pattern as ProposalsBanner).
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { CalendarClock, ChevronLeft, ChevronRight } from "lucide-react";
import { useChatContext } from "./providers/ChatProvider";
import { useNotificationsOptional } from "../../notifications/NotificationsProvider";

export function MeetingKbBanner() {
  const { granolaPending } = useChatContext();
  const { meetings, accept, dismiss, loading, error, retry } = granolaPending;
  const notifications = useNotificationsOptional();
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  // Keep index in bounds when meetings shrink after accept/dismiss.
  const safeIndex = Math.min(index, Math.max(0, meetings.length - 1));
  const meeting = meetings[safeIndex] ?? null;

  if (!meeting) {
    // An initial-load failure must stay visible (with a way to recover)
    // rather than silently dropping the whole review workflow.
    if (loading || !error) return null;
    return (
      <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Paper elevation={6} data-ph-scope="meeting_banner" sx={{ p: 2, width: 320, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <CalendarClock size={16} aria-hidden style={{ flexShrink: 0, opacity: 0.8 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
              Couldn't load meetings
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {error}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button size="small" data-ph="refresh" onClick={retry}>
              Retry
            </Button>
          </Box>
        </Paper>
      </Snackbar>
    );
  }

  const total = meetings.length;

  // Move index back if we were on the last item, mirroring ProposalsBanner.
  const stepBack = () =>
    setIndex((i) => Math.max(0, i - (i >= meetings.length - 1 ? 1 : 0)));

  const handleDismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await dismiss(meeting.id);
      stepBack();
    } catch (exc) {
      notifications?.notify(
        exc instanceof Error ? exc.message : "Failed to dismiss meeting.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await accept(meeting.id);
      notifications?.notify(`"${meeting.title}" added to the Knowledge Base.`, "success");
      stepBack();
    } catch (exc) {
      notifications?.notify(
        exc instanceof Error ? exc.message : "Failed to add meeting to the Knowledge Base.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
      <Paper elevation={6} data-ph-scope="meeting_banner" sx={{ p: 2, width: 320, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <CalendarClock size={16} aria-hidden style={{ flexShrink: 0, opacity: 0.8 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
            New meeting available
          </Typography>
          {total > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Previous"
                    data-ph="back"
                    onClick={() => setIndex((i) => i - 1)}
                    disabled={busy || safeIndex === 0}
                    sx={{ p: 0.25 }}
                  >
                    <ChevronLeft size={14} />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" sx={{ opacity: 0.6, userSelect: "none" }}>
                {safeIndex + 1}/{total}
              </Typography>
              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Next"
                    data-ph="next"
                    onClick={() => setIndex((i) => i + 1)}
                    disabled={busy || safeIndex === total - 1}
                    sx={{ p: 0.25 }}
                  >
                    <ChevronRight size={14} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {meeting.title}
        </Typography>
        {meeting.summary && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mb: 0.5,
            }}
          >
            {meeting.summary}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Add this meeting to the AI Knowledge Base?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
          <Button size="small" color="inherit" data-ph="reject" onClick={handleDismiss} disabled={busy}>
            Dismiss
          </Button>
          <Button size="small" variant="contained" data-ph="approve" onClick={handleAdd} disabled={busy}>
            {busy ? <CircularProgress size={13} /> : "Add to Knowledge Base"}
          </Button>
        </Box>
      </Paper>
    </Snackbar>
  );
}
